import { StationApi } from 'api/StationApi'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { useEffect, useMemo, useState } from 'react'

import type { useStationControlBoard } from '../../StationControl/useStationControlBoard'
import detailsStyle from '../StationDetails.module.css'
import style from './StationControlSection.module.css'

const PUMP_LABELS: Record<string, string> = {
  PUMP_1: 'Pump 1',
  PUMP_2: 'Pump 2',
}

const CTRL_REASON_LABELS: Record<string, string> = {
  ctrl_disabled_by_flag: 'Control ops not enabled on server',
  offline: 'Station offline',
  no_telemetry_snapshot: 'No telemetry snapshot',
}

type BoardState = ReturnType<typeof useStationControlBoard>

type StationControlSectionProps = {
  boardState: BoardState
  stationId: number
}

const StationControlSection = ({ boardState, stationId }: StationControlSectionProps) => {
  const { lotGate, uiLocked, awaitingInProgress, softwareUpdateInProgress, runOp } =
    boardState
  const [pumpTarget, setPumpTarget] = useState<'PUMP_1' | 'PUMP_2' | ''>('')
  const [confirmForceOpen, setConfirmForceOpen] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)

  const disabledReasons = useMemo(
    () =>
      (lotGate?.ctrlDisabledReasons || [])
        .map(reason => CTRL_REASON_LABELS[reason] || reason)
        .filter(Boolean)
        .join('; '),
    [lotGate?.ctrlDisabledReasons]
  )

  const pumpOptions: IOption[] = useMemo(() => {
    const targets = lotGate?.clearOverpressureTargets || []
    return targets.map(target => ({
      value: target,
      text: PUMP_LABELS[target] || target,
    }))
  }, [lotGate?.clearOverpressureTargets])

  useEffect(() => {
    if (!pumpOptions.length) {
      setPumpTarget('')
      return
    }
    const values = pumpOptions.map(opt => String(opt.value))
    if (!pumpTarget || !values.includes(pumpTarget)) {
      const first = String(pumpOptions[0].value)
      if (first === 'PUMP_1' || first === 'PUMP_2') {
        setPumpTarget(first)
      }
    }
  }, [pumpOptions, pumpTarget])

  const pumpOption = useMemo(
    () => pumpOptions.find(opt => opt.value === pumpTarget) ?? pumpOptions[0],
    [pumpOptions, pumpTarget]
  )

  const cancelBlocked = !lotGate?.forceCancelAllowed || uiLocked
  const forceBlocked = !lotGate?.forceReleaseAllowed || uiLocked
  const clearBlocked = !lotGate?.clearOverpressureAllowed || uiLocked || !pumpTarget
  const statusText = lotGate?.pending
    ? 'Pending'
    : lotGate?.occupied && lotGate.robotSn != null
      ? String(lotGate.robotSn)
      : 'Released'

  const lagHint = softwareUpdateInProgress
    ? 'Software update in progress'
    : awaitingInProgress
      ? 'Stage starting — wait until IN_PROGRESS'
      : undefined
  const cancelTitle =
    lagHint || lotGate?.forceCancelBlockedReason || disabledReasons || undefined
  const forceTitle =
    lagHint || lotGate?.forceReleaseBlockedReason || disabledReasons || undefined
  const clearTitle =
    lagHint || lotGate?.clearOverpressureBlockedReason || disabledReasons || undefined

  return (
    <div className={style.root}>
      <div className={detailsStyle.tableHeaderRow}>
        <div className={detailsStyle.centered}>Service Control</div>
      </div>

      {lotGate?.forceCancelSupported ? (
        <div
          className={detailsStyle.tableRow}
          style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}
        >
          <div className={detailsStyle.centered} aria-hidden />
          <div className={detailsStyle.textAlignLeft}>Force cancel</div>
          <div className={detailsStyle.centered} title={cancelTitle}>
            <Button.Standart
              className={detailsStyle.controlButton}
              disabled={cancelBlocked}
              loading={uiLocked}
              onClick={() => setConfirmCancelOpen(true)}
              title={cancelTitle}
            >
              Cancel
            </Button.Standart>
          </div>
          <div className={detailsStyle.percents} title={cancelTitle}>
            {lotGate?.inProgress ? 'Stage running' : '\u00a0'}
          </div>
        </div>
      ) : null}

      <div
        className={detailsStyle.tableRow}
        style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}
      >
        <div className={detailsStyle.centered} aria-hidden />
        <div className={detailsStyle.textAlignLeft}>Force release</div>
        <div className={detailsStyle.centered} title={forceTitle}>
          <Button.Standart
            className={detailsStyle.controlButton}
            disabled={forceBlocked}
            loading={uiLocked}
            onClick={() => setConfirmForceOpen(true)}
            title={forceTitle}
          >
            Release
          </Button.Standart>
        </div>
        <div className={detailsStyle.percents} title={forceTitle}>
          {statusText || '\u00a0'}
        </div>
      </div>

      {lotGate?.clearOverpressureSupported ? (
        <div
          className={detailsStyle.tableRow}
          style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}
        >
          <div className={detailsStyle.centered} aria-hidden />
          <div className={detailsStyle.textAlignLeft}>Clear overpressure</div>
          <div className={detailsStyle.centered} title={clearTitle}>
            <Button.Standart
              className={detailsStyle.controlButton}
              disabled={clearBlocked}
              loading={uiLocked}
              onClick={() => {
                if (lotGate?.lotId == null || !pumpTarget) return
                void runOp(() =>
                  StationApi.controlAction(stationId, {
                    action: 'CLEAR_OVERPRESSURE',
                    lot_id: lotGate.lotId!,
                    target: pumpTarget,
                  })
                )
              }}
              title={clearTitle}
            >
              Clear
            </Button.Standart>
          </div>
          <div className={style.stageCell}>
            <Dropdown
              className={style.stageDropdown}
              disabled={clearBlocked || pumpOptions.length === 0}
              options={pumpOptions}
              currentOption={pumpOption}
              placeholder="Pump"
              noShadow
              onClickOption={option => {
                const value = String(option.value || '')
                if (value === 'PUMP_1' || value === 'PUMP_2') {
                  setPumpTarget(value)
                }
              }}
            />
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        title="Force-cancel stage?"
        isOpen={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        onConfirmClick={() => {
          setConfirmCancelOpen(false)
          if (lotGate?.lotId == null) return
          void runOp(() =>
            StationApi.controlAction(stationId, {
              action: 'FORCE_CANCEL',
              lot_id: lotGate.lotId!,
            })
          )
        }}
        confirmText="Force cancel"
        danger
      >
        <div className={style.confirmBody}>
          Stops the current maintenance stage and resets station hardware. The lot stays occupied.
          Use Force release afterwards if the session itself must be freed.
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        title="Force-release lot?"
        isOpen={confirmForceOpen}
        onClose={() => setConfirmForceOpen(false)}
        onConfirmClick={() => {
          setConfirmForceOpen(false)
          if (lotGate?.lotId == null) return
          void runOp(() =>
            StationApi.controlAction(stationId, {
              action: 'FORCE_RELEASE',
              lot_id: lotGate.lotId!,
            })
          )
        }}
        confirmText="Force release"
        danger
      >
        <div className={style.confirmBody}>
          This frees the lot without the occupying robot&apos;s RELEASE. It does not cancel a running
          stage — Force cancel (or robot CANCEL) first if the stage is IN_PROGRESS.
        </div>
      </ConfirmDialog>
    </div>
  )
}

export default StationControlSection
